/**
 * Wohin ein frisch abgelegter **Notiz** gehört.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: „mittig" und „gerastert"
 * sollen ohne gerenderten Canvas prüfbar sein.
 */

import { snapToGrid } from './canvasAlignment'
import { DEFAULT_CANVAS_NOTE_WIDTH, DEFAULT_CANVAS_NOTE_HEIGHT } from './canvasNoteStyle'
import { DEFAULT_MEMO_WIDTH, MEMO_MIN_HEIGHT } from './canvasMemoStyle'
import { DEFAULT_AREA_WIDTH, DEFAULT_AREA_HEIGHT } from './canvasArea'
import { CANVAS_TEXT_PLACEHOLDER_ESTIMATE, DEFAULT_CANVAS_TEXT_SIZE, estimateCanvasTextSize } from './canvasText'

/**
 * Die Maße, mit denen ein frisch abgesetztes **Canvas-Element** ANGELEGT
 * wird — eine Notiz breiter (`DEFAULT_MEMO_WIDTH`) und in ihrer
 * Anlege-Höhe (`MEMO_MIN_HEIGHT`), eine Notiz quadratisch.
 *
 * **Die EINZIGE Stelle, die diese Fallunterscheidung trifft.** Sowohl das
 * tatsächliche Ablegen (`placeNoteAt`) als auch das Vorschaubild-Rastern
 * (`previewPoint`, beide in `CanvasFlow.tsx`) rufen hierher durch — vorher
 * stand dieselbe Verzweigung zweimal da, und die zweite Stelle war beim
 * Hinzufügen der Notiz nicht mitgezogen worden: Sie rechnete weiter mit der
 * Breite der Notiz (168 statt 249), das Vorschaubild rastete also woanders
 * ein als die Notiz dann tatsächlich landete.
 *
 * Dient inzwischen auch als Antwort auf eine zweite, verwandte Frage: „wie
 * groß ist ein Canvas-Element dieser Art, wenn die Zeile selbst keine Maße
 * trägt?" — `CanvasFlow.noteToFlowNode` und `MemoNode` fragen sie beim
 * Rendern genauso ab. Beide Fragen sind heute praktisch unerreichbar (jeder
 * Anlegeweg setzt eine Breite), sollen aber nicht wieder auseinanderlaufen.
 */
export function getPlacementSize(kind: 'note' | 'memo' | 'area' | 'text'): { width: number; height: number } {
  if (kind === 'area') return { width: DEFAULT_AREA_WIDTH, height: DEFAULT_AREA_HEIGHT }
  // Der **Text** in seiner Vorgabegröße, als leerer Platzhalter geschätzt — er
  // wird ohnehin sofort gemessen (seine Maße folgen dem Inhalt).
  if (kind === 'text') return estimateCanvasTextSize(CANVAS_TEXT_PLACEHOLDER_ESTIMATE, DEFAULT_CANVAS_TEXT_SIZE)
  return kind === 'memo'
    ? { width: DEFAULT_MEMO_WIDTH, height: MEMO_MIN_HEIGHT }
    : { width: DEFAULT_CANVAS_NOTE_WIDTH, height: DEFAULT_CANVAS_NOTE_HEIGHT }
}

/**
 * Die Lage eines neuen Notiz, wenn der Zeiger HIER steht.
 *
 * **Mittig um den Zeiger**, nicht mit der linken oberen Ecke daran: Wer eine
 * Notiz ablegt, meint die Stelle, an der sie liegen soll, nicht die Ecke, an
 * der sie anfängt. Beim Ziehen sieht man dasselbe — das Vorschaubild hängt
 * mittig am Zeiger.
 *
 * **Danach aufs Punktraster**, wie alles andere auf dem Canvas (Verschieben,
 * Skalieren, Duplizieren). Vorher war der frisch abgelegte Notiz das einzige
 * Element, das daneben lag, und rutschte erst beim ersten Verschieben darauf.
 *
 * @param pointer Zeigerstelle in CANVAS-Koordinaten
 * @param size    Maße der Notiz
 * @param grid    Rasterweite; 0 lässt die Position stufenlos
 */
export function getPlacementPosition(
  pointer: { x: number; y: number },
  size: { width: number; height: number },
  grid: number,
): { x: number; y: number } {
  return {
    x: snapToGrid(pointer.x - size.width / 2, grid),
    y: snapToGrid(pointer.y - size.height / 2, grid),
  }
}

/**
 * Wie weit der Zeiger vom Werkzeug-Knopf weg muss, bevor aus dem Drücken ein
 * ZUG wird — in Bildschirmpixeln, gemessen in jeder Richtung.
 *
 * **Ohne sie ist jeder Klick ein Zug**, und genau daran hing der Fehler: Das
 * Loslassen legte sofort eine leere Notiz ab, während der Klick daneben noch
 * ein zweites Mal scharfstellte. Man bekam beides — eine Notiz unter dem Knopf
 * UND ein Vorschaubild am Zeiger.
 *
 * **12 und nicht 3 wie am Drag Handle.** Dort fängt die Schwelle nur das
 * Zittern beim Klicken ab und darf klein sein, weil ein Loslassen auf dem Griff
 * ohnehin nichts schreibt. Hier ist sie im GESCHLOSSENEN Canvas die einzige
 * Absicherung: Das Drücken öffnet den Canvas, die Pille wandert unter dem
 * Zeiger weg, und das Loslassen fällt dann wirklich auf die Fläche — kein
 * Zielvergleich kann das noch abfangen, nur die Frage „hat sich die Hand
 * bewegt?". Dieselbe Zahl wie `DUPLICATE_DRAG_THRESHOLD`, aus demselben Grund:
 * sicher jenseits des Handzitterns, und fühlt sich noch sofort an.
 *
 * **Sie verzögert nichts Sichtbares.** Das Vorschaubild hängt an
 * `placementMode` und erscheint schon beim Aufsetzen; die Schwelle entscheidet
 * allein, was das LOSLASSEN tut.
 *
 * Anders als `DUPLICATE_DRAG_THRESHOLD` NICHT gegen den Zoom zu rechnen: Der
 * Zug beginnt auf einem Knopf, der seine Größe behält, und fragt nach einer
 * Handbewegung — nicht nach einer Strecke auf der Fläche.
 */
export const PLACEMENT_DRAG_THRESHOLD = 12

/**
 * Ob sich der Zeiger seit dem Aufsetzen weit genug bewegt hat, dass es ein Zug
 * ist.
 *
 * Gemessen wird die Bewegung des ZEIGERS, nicht ob er den Knopf noch berührt:
 * Im geschlossenen Canvas wandert die Pille unter dem stehenden Zeiger weg, und
 * das ist keine Handbewegung.
 *
 * @example
 * isPlacementDrag({ x: 100, y: 100 }, { x: 104, y: 100 })   // false — ein Klick
 * isPlacementDrag({ x: 100, y: 100 }, { x: 100, y: 130 })   // true  — ein Zug
 */
export function isPlacementDrag(
  origin: { x: number; y: number },
  pointer: { x: number; y: number },
): boolean {
  return Math.hypot(pointer.x - origin.x, pointer.y - origin.y) >= PLACEMENT_DRAG_THRESHOLD
}

/**
 * Horcht nach dem Anfassen eines Platzier-Knopfs, ob daraus ein Zug wird:
 * `onDrag` kommt einmal, sobald die Hand die Schwelle überschreitet. Danach,
 * oder mit dem Loslassen, hört es auf.
 *
 * Am `window`, nicht am Knopf: Der Knopf kann unter der Hand verschwinden —
 * die Listen-Werkzeug-Pille taucht ab, der leere Zustand geht ganz.
 *
 * `pointercancel` zählt wie das Loslassen, sonst bliebe die Vormerkung hängen.
 *
 * @example
 * watchPlacementDrag({ x: e.clientX, y: e.clientY }, () => setPlacementDrag(true))
 */
export function watchPlacementDrag(origin: { x: number; y: number }, onDrag: () => void): void {
  const handleMove = (event: PointerEvent) => {
    if (!isPlacementDrag(origin, { x: event.clientX, y: event.clientY })) return
    stop()
    onDrag()
  }
  const stop = () => {
    window.removeEventListener('pointermove', handleMove)
    window.removeEventListener('pointerup', stop)
    window.removeEventListener('pointercancel', stop)
  }

  window.addEventListener('pointermove', handleMove)
  window.addEventListener('pointerup', stop)
  window.addEventListener('pointercancel', stop)
}
